import axios, {AxiosResponse} from "axios";
import Customer from "@/model/Customer";
import CustomerType from "@/model/CustomerType";
import {CustomerClass} from "@/enums";
import {SpringPagedResult} from "@/service/PagedResult";

const PATH = "/v2/customers";
const CUSTOMER_PAGE_SIZE = 1000;

export enum CustomerFeatureType {
    FRUIT_AND_VEGETABLES = "FRUIT_AND_VEGETABLES",
    FLOWERS = "FLOWERS",
    STORAGE = "STORAGE",
    BEVERAGE_MARKET = "BEVERAGE_MARKET",
    TARGET_ACTUAL_COMPARISON = "TARGET_ACTUAL_COMPARISON",
    LABELS = "LABELS"
}

/**
 * A customer as the v2 API returns it. `id` is the customer's uuid; the numeric id is the deprecated `legacyId`.
 */
export interface CustomerV2Dto {
    id: string;
    legacyId: number;
    active: boolean;
    name: string;
    customerNumber?: string | null;
    customerClass: CustomerClass;
    customerType: {
        id: number;
        name: string;
    };
    region?: string | null;
    sellingArea?: string | null;
    costCenter?: string | null;
    company?: string | null;
    address: {
        street: string;
        zip?: string | null;
        city?: string | null;
    };
    phone?: string | null;
    mobile?: string | null;
    fax?: string | null;
    email?: string | null;
    auditEmail?: string | null;
    storeManager?: string | null;
    storeAssistant?: string | null;
    features: Array<{
        type: CustomerFeatureType;
        notes?: string | null;
    }>;
    additionalInformation?: string | null;
    doorCode?: string | null;
    inventoryValue?: number | null;
    positions?: number | null;
}

interface CustomerV2CreateRequest {
    name: string;
    customerNumber?: string;
    customerClass: CustomerClass;
    customerTypeId: number;
    region?: string;
    sellingArea?: string;
    costCenter?: string;
    company?: string;
    address: {
        street: string;
        zip?: string;
        city?: string;
    };
    phone?: string;
    mobile?: string;
    fax?: string;
    email?: string;
    auditEmail?: string;
    storeManager?: string;
    storeAssistant?: string;
    features: Array<{
        type: CustomerFeatureType;
        notes?: string;
    }>;
    additionalInformation?: string;
    doorCode?: string;
    inventoryValue?: number;
    positions?: number;
}

class CustomerServiceV2 {

  createCustomer(customer: Customer): Promise<Customer> {
    if (!customer.customerClass) {
      return Promise.reject(new Error("Customer class is required"));
    }

    return axios.post<CustomerV2Dto>(PATH, this.toCreateRequest(customer, customer.customerClass))
      .then(response => this.toCustomer(response.data));
  }

  /**
   * Deactivates (soft-deletes) a customer via POST /v2/customers/{uuid}/deactivate. Answers 204 on success and 409 if
   * the customer is already inactive.
   */
  deactivateCustomer(uuid: string): Promise<AxiosResponse<void>> {
    return axios.post<void>(`${PATH}/${uuid}/deactivate`);
  }

  /**
   * The active customers, ordered by name, loading every page of GET /v2/customers. Mapped to {@link Customer}s
   * addressed by the deprecated legacyId, so they can still be opened via the v1 customer details.
   */
  findActiveCustomers(): Promise<Array<Customer>> {
    return this.findActiveCustomersFrom(0, [])
      .then(customers => customers.map(customer => this.toCustomer(customer)));
  }

  private findActiveCustomersFrom(page: number, customers: Array<CustomerV2Dto>): Promise<Array<CustomerV2Dto>> {
    return axios.get<SpringPagedResult<CustomerV2Dto>>(PATH, {
      params: {
        active: true,
        page: page,
        size: CUSTOMER_PAGE_SIZE
      }
    }).then(response => {
      const loaded = customers.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.findActiveCustomersFrom(page + 1, loaded)
        : loaded;
    });
  }

  private toCustomer(customer: CustomerV2Dto): Customer {
    const feature = (type: CustomerFeatureType) => customer.features.find(f => f.type === type);
    return new Customer({
      id: customer.legacyId,
      uuid: customer.id,
      active: customer.active,
      name: customer.name,
      customerId: customer.customerNumber,
      customerClass: customer.customerClass,
      customerType: new CustomerType(customer.customerType),
      region: customer.region,
      sellingArea: customer.sellingArea,
      costCenter: customer.costCenter,
      company: customer.company,
      address: customer.address,
      phone: customer.phone,
      mobile: customer.mobile,
      fax: customer.fax,
      email: customer.email,
      auditEmail: customer.auditEmail,
      storeManager: customer.storeManager,
      storeAssistant: customer.storeAssistant,
      og: !!feature(CustomerFeatureType.FRUIT_AND_VEGETABLES),
      notesOg: feature(CustomerFeatureType.FRUIT_AND_VEGETABLES)?.notes,
      flowers: !!feature(CustomerFeatureType.FLOWERS),
      notesFlowers: feature(CustomerFeatureType.FLOWERS)?.notes,
      storage: !!feature(CustomerFeatureType.STORAGE),
      notesStorage: feature(CustomerFeatureType.STORAGE)?.notes,
      drinks: !!feature(CustomerFeatureType.BEVERAGE_MARKET),
      notesDrinks: feature(CustomerFeatureType.BEVERAGE_MARKET)?.notes,
      targetActualComparison: !!feature(CustomerFeatureType.TARGET_ACTUAL_COMPARISON),
      labels: !!feature(CustomerFeatureType.LABELS),
      additionalInformation: customer.additionalInformation,
      doorcode: customer.doorCode,
      inventoryValue: customer.inventoryValue,
      positions: customer.positions
    });
  }

  private toCreateRequest(customer: Customer, customerClass: CustomerClass): CustomerV2CreateRequest {
    const features = [
      {type: CustomerFeatureType.FRUIT_AND_VEGETABLES, notes: customer.notesOg, enabled: customer.og},
      {type: CustomerFeatureType.FLOWERS, notes: customer.notesFlowers, enabled: customer.flowers},
      {type: CustomerFeatureType.STORAGE, notes: customer.notesStorage, enabled: customer.storage},
      {type: CustomerFeatureType.BEVERAGE_MARKET, notes: customer.notesDrinks, enabled: customer.drinks},
      {type: CustomerFeatureType.TARGET_ACTUAL_COMPARISON, enabled: customer.targetActualComparison},
      {type: CustomerFeatureType.LABELS, enabled: customer.labels}
    ]
      .filter(feature => feature.enabled)
      .map(feature => ({type: feature.type, notes: feature.notes}));

    const inventoryValue = customer.inventoryValue ? Number(customer.inventoryValue) : undefined;

    return {
      name: customer.name,
      customerNumber: customer.customerId,
      customerClass,
      customerTypeId: customer.customerTypeId,
      region: customer.region?.toString(),
      sellingArea: customer.sellingArea?.toString(),
      costCenter: customer.costCenter?.toString(),
      company: customer.company,
      address: {
        street: customer.address.street,
        zip: customer.address.zip?.toString(),
        city: customer.address.city
      },
      phone: customer.phone,
      mobile: customer.mobile,
      fax: customer.fax,
      email: customer.email,
      auditEmail: customer.auditEmail,
      storeManager: customer.storeManager,
      storeAssistant: customer.storeAssistant,
      features,
      additionalInformation: customer.additionalInformation,
      doorCode: customer.doorcode,
      inventoryValue,
      positions: customer.positions
    };
  }
}

export default new CustomerServiceV2();
